import React, { useMemo, useState } from 'react';

type Variant = 'info' | 'success' | 'warning' | 'accent';
type Position = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export interface FeaturesEnabledRibbonProps {
    features: string[]; // list of enabled feature names
    variant?: Variant;
    position?: Position;
    dismissible?: boolean;
    onDismiss?: () => void;
    ariaLabel?: string;
}

/**
 * FeaturesEnabledRibbon
 *
 * Renders a small diagonal ribbon anchored to a corner that highlights that
 * specific features are enabled. The ribbon is dismissible (optional) and
 * accessible.
 *
 * Usage:
 * <FeaturesEnabledRibbon features={['Beta Chat', 'New UI']} dismissible />
 */
const COLOR_MAP: Record<Variant, string> = {
    info: '#2563eb',
    success: '#16a34a',
    warning: '#f59e0b',
    accent: '#7c3aed'
};

const POSITION_STYLES: Record<
    Position,
    { container: React.CSSProperties; ribbon: React.CSSProperties }
> = {
    'top-right': {
        container: { top: 16, right: -36 },
        ribbon: { transform: 'rotate(45deg)' }
    },
    'top-left': {
        container: { top: 8, left: 8 },
        ribbon: { transform: 'rotate(-45deg)' }
    },
    'bottom-right': {
        container: { bottom: 8, right: 8 },
        ribbon: { transform: 'rotate(-45deg)' }
    },
    'bottom-left': {
        container: { bottom: 8, left: 8 },
        ribbon: { transform: 'rotate(45deg)' }
    }
};

export default function FeaturesEnabledRibbon({
    features,
    variant = 'accent',
    position = 'top-right',
    dismissible = false,
    onDismiss,
    ariaLabel
}: FeaturesEnabledRibbonProps) {
    const [visible, setVisible] = useState(true);

    const color = COLOR_MAP[variant];
    const pos = POSITION_STYLES[position];

    const label = useMemo(() => {
        if (!features || features.length === 0) return '';
        if (features.length === 1) return features[0];
        if (features.length === 2) return `${features[0]} and ${features[1]}`;
        return `${features[0]} and ${features.length - 1} more`;
    }, [features]);

    if (!visible || !features || features.length === 0) return null;

    const handleDismiss = () => {
        setVisible(false);
        onDismiss?.();
    };

    return (
        <div
            aria-live="polite"
            role="status"
            style={{
                position: 'fixed',
                zIndex: 9999,
                pointerEvents: 'none',
                ...pos.container
            }}
        >
            <div
                style={{
                    pointerEvents: 'auto',
                    display: 'inline-block',
                    padding: '6px 12px',
                    color: 'white',
                    background: color,
                    borderRadius: 4,
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: 1,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    transformOrigin: 'center',
                    ...pos.ribbon,
                    // ensure the ribbon sits visually like a corner banner
                    transformBox: 'fill-box' as any,
                    whiteSpace: 'nowrap'
                }}
                aria-label={ariaLabel ?? `Features enabled: ${features.join(', ')}`}
                title={features.join(', ')}
            >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'inline-block', paddingRight: 6 }}>
                        Engage 2 {label}
                    </span>
                    {dismissible && (
                        <button
                            onClick={handleDismiss}
                            aria-label="Dismiss features ribbon"
                            style={{
                                appearance: 'none',
                                border: 'none',
                                background: 'rgba(255,255,255,0.18)',
                                color: 'white',
                                width: 20,
                                height: 20,
                                borderRadius: 3,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 700,
                                padding: 0,
                                lineHeight: 1
                            }}
                        >
                            ×
                        </button>
                    )}
                </span>
            </div>
        </div>
    );
}
